import BuildConfig from '$extensionSrc/buildConfig'

import { YOUTUBE_AUTOMATION_STAGE_STATUSES } from './youtubeAutomationStageStatuses'
import { getToggle } from './youtubeSettingsUtils'
import * as youtubeUrls from './youtubeUrls'
import { YoutubeSettingKey } from '../../recommendations/recommendationSettingsKeys'
import { StageType, YoutubeRecommendationKey } from '../../utils/enums'
import { StageDef } from '../automationMachineInterfaces'
import { wait } from '../interruptiblePromises'
import {
  defaultCantUndoFn,
  getCurrentValueFnFromKey,
  getIsFulfilledFnFromKey,
} from '../recommendationCardDisplayUtils'
import { shouldUpdateSetting } from '../utils'

// Settings properties
const getCurrentValueFn = (key) =>
  getCurrentValueFnFromKey(key, (value) => (value === 'true' ? 'On' : 'Off'))

const getIsFulfilledFn = (key) =>
  getIsFulfilledFnFromKey(key, (value) => value === 'false')

const getCantUndoFn = () => defaultCantUndoFn
const getIsDisabledFn = (settingsKey) => (automationErrors) =>
  Boolean(automationErrors?.[settingsKey])
const getIsDisabledReasonFn = (settingsKey) => (automationErrors) => {
  const errorObj = automationErrors?.[settingsKey]

  if (!errorObj) {
    return null
  }

  if (errorObj?.gatherError?.message?.startsWith('getElement')) {
    return `${BuildConfig.TENANT_NAME} couldn't find your current setting`
  }
  return 'Unknown error'
}

const notificationLabels = {
  [YoutubeSettingKey.PUSH_NOTIFICATION_CHANNEL_ACTIVITY]:
    'activity on my channel',
  [YoutubeSettingKey.PUSH_NOTIFICATION_COMMENT_REPLIES]:
    'activity on my comments',
  [YoutubeSettingKey.PUSH_NOTIFICATION_MENTIONS]: 'mentions',
  [YoutubeSettingKey.PUSH_NOTIFICATION_PROMOTIONAL_CONTENT]:
    'promotional content and offerings',
  [YoutubeSettingKey.PUSH_NOTIFICATION_RECOMMENDED_VIDEOS]:
    'recommended videos',
  [YoutubeSettingKey.PUSH_NOTIFICATION_OTHERS_REUSING_MY_CONTENT]:
    'others reusing my content',
  [YoutubeSettingKey.PUSH_NOTIFICATION_SUBSCRIPTIONS]: 'subscriptions',
}

// Stage definitions
enum StageKey {
  GATHER_SETTINGS_PUSH_NOTIFICATIONS = 'GATHER_SETTINGS_PUSH_NOTIFICATIONS',
  UPDATE_SETTINGS_PUSH_NOTIFIATIONS = 'UPDATE_SETTINGS_PUSH_NOTIFICATIONS',
  UNDO_SETTINGS_PUSH_NOTIFICATIONS = 'UNDO_SETTINGS_PUSH_NOTIFICATIONS',
}

const gatherStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: StageKey.GATHER_SETTINGS_PUSH_NOTIFICATIONS,
    status: YOUTUBE_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: youtubeUrls.notificationsUrl,
    action: async (machineContext, userContext) => {
      const { updateStoredAutomationErrors, updateStoredInitialValues } =
        userContext

      for (const [setting, label] of Object.entries(notificationLabels)) {
        let settingToggle
        try {
          settingToggle = await getToggle(label)
        } catch (error) {
          if (error?.message && error.message.startsWith('getElement')) {
            await updateStoredAutomationErrors(
              {
                [setting]: {
                  gatherError: { message: error.message, name: error.name },
                },
              },
              YoutubeRecommendationKey.YOUTUBE_PUSH_NOTIFICATIONS,
            )
          }
        }

        if (settingToggle) {
          await updateStoredInitialValues(
            {
              [setting]: settingToggle.ariaPressed,
            },
            YoutubeRecommendationKey.YOUTUBE_PUSH_NOTIFICATIONS,
          )
        }
      }
    },
  },
]

const updateStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: StageKey.UPDATE_SETTINGS_PUSH_NOTIFIATIONS,
    status: YOUTUBE_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: youtubeUrls.notificationsUrl,
    action: async (machineContext, userContext) => {
      const { automationErrors, isDry, initialValues, settingsSelected } =
        userContext

      for (const [setting, label] of Object.entries(notificationLabels)) {
        if (
          shouldUpdateSetting(
            settingsSelected,
            initialValues,
            setting,
            getIsFulfilledFn,
          ) &&
          !getIsDisabledFn(setting)(automationErrors)
        ) {
          const settingToggle = await getToggle(label)

          if (settingToggle.ariaPressed === 'true' && !isDry) {
            settingToggle.click()
            await wait(500)
          }
        }
      }
    },
  },
]

const undoStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: StageKey.UPDATE_SETTINGS_PUSH_NOTIFIATIONS,
    status: YOUTUBE_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: youtubeUrls.notificationsUrl,
    action: async (machineContext, userContext) => {
      const { automationErrors, isDry, initialValues, settingsSelected } =
        userContext

      for (const [setting, label] of Object.entries(notificationLabels)) {
        if (
          settingsSelected[setting] &&
          initialValues[setting] &&
          !getIsDisabledFn(setting)(automationErrors)
        ) {
          const settingToggle = await getToggle(label)

          if (settingToggle.ariaPressed !== initialValues[setting] && !isDry) {
            settingToggle.click()
            await wait(500)
          }
        }
      }
    },
  },
]

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
